import { MessageOutlined, WhatsAppOutlined } from '@ant-design/icons';
import type { NotifyChannel, PhoneStatus } from '@horof/shared';
import {
  DOCUMENT_TITLES,
  documentMessage,
  type MemberDocument,
  smsLink,
  whatsappLink,
} from '@horof/shared';
import { Button } from 'antd';

type Props = {
  member: {
    fullName: string | null;
    notifyPhone: string | null;
    phoneStatus: PhoneStatus | null;
    notifyChannel: NotifyChannel;
    statementLink: string;
    noticeLink: string;
    unpaidCount: number;
  };
  associationName: string;
};

/**
 * Open a document, or send it from the committee member's own WhatsApp or
 * SMS app with the message ready (they only press send). Sending follows the
 * member's choice: a valid mobile number, their preferred channel first, and
 * nothing if they asked not to be messaged.
 */
export function MemberDocuments({ member, associationName }: Props) {
  const documents: [MemberDocument, string][] = [
    ['statement', member.statementLink],
    ...(member.unpaidCount > 0
      ? [['notice', member.noticeLink] as [MemberDocument, string]]
      : []),
  ];
  const phone = member.phoneStatus === 'OK' ? member.notifyPhone : null;
  const canSend = phone !== null && member.notifyChannel !== 'NONE';
  const smsFirst = member.notifyChannel === 'SMS';

  return (
    <section className="card">
      <div className="field-label">الوثائق</div>
      {documents.map(([document, link]) => {
        const text = documentMessage(
          document,
          member.fullName,
          associationName,
          link,
        );
        const whatsapp = phone && (
          <Button
            key="whatsapp"
            href={whatsappLink(phone, text)}
            target="_blank"
            icon={<WhatsAppOutlined />}
            className={smsFirst ? undefined : 'whatsapp-button'}
          >
            واتساب
          </Button>
        );
        const sms = phone && (
          <Button
            key="sms"
            href={smsLink(phone, text)}
            icon={<MessageOutlined />}
            type={smsFirst ? 'primary' : 'default'}
          >
            رسالة قصيرة
          </Button>
        );
        return (
          <div key={document} className="document-row">
            <strong>{DOCUMENT_TITLES[document]}</strong>
            <div className="document-actions">
              <Button href={link} target="_blank">
                فتح
              </Button>
              {canSend && (smsFirst ? [sms, whatsapp] : [whatsapp, sms])}
            </div>
          </div>
        );
      })}
      {!canSend && (
        <div className="field-hint">
          {member.notifyChannel === 'NONE'
            ? 'طلب العضو ألا تُرسل إليه رسائل: افتح الوثيقة واطبعها.'
            : 'لا رقم هاتف محمول صالح: افتح الوثيقة واطبعها، أو صحّح الرقم.'}
        </div>
      )}
    </section>
  );
}
